import { Info } from "lucide-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { buttonVariants } from "@/app/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/app/components/ui/tooltip";
import { LevelsOfExperience } from "../../models/resume/template";

interface ExperienceLevel {
	name: string;
	description: string;
	showInList: boolean;
}

const careerPathTemplates = [
	{
		image: "/images/resume-template/template-1.png",
		title: "College Resume Template",
		description: "No experience? No problem! We'll guide you.",
	},
	{
		image: "/images/resume-template/template-9.png",
		title: "Professional Resume Template",
		description: "Communicate your value confidently!",
	},
	{
		image: "/images/resume-template/template-10.png",
		title: "Expertly Crafted Template",
		description: "Say more with less!",
	},
];

function SelectExperience() {
	const levelsObject = new LevelsOfExperience();
	const listOfExperience: ExperienceLevel[] = levelsObject.listOfExperience;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const template = useSelector((state: { TemplateSelectionReducer: { navigationFlow: unknown } }) => {
		return state.TemplateSelectionReducer;
	});
	const navigationFlow = template.navigationFlow;

	const [showTemplates, setShowTemplates] = useState(false);

	function onSelectionClick(levelName: string) {
		localStorage.setItem("yearsOfExperience", levelName);

		dispatch({
			type: "",
			payload: {
				selectedTemplate: {
					yearsOfExperience: levelName,
					templateName: "",
				},
				navigationFlow: navigationFlow,
			},
		});

		navigate("/resume/select-template");
	}

	return (
		<>
			<div className="mb-6">
				<h2 className="text-2xl font-bold text-gray-900 md:text-3xl">How long have you been working?</h2>
				<p className="mt-2 flex items-center gap-1 text-gray-600">
					We will find the best templates for your experience level.{" "}
					<Tooltip>
						<TooltipTrigger
							className="cursor-pointer p-0 text-base"
							onClick={() => setShowTemplates(true)}
							aria-label="Learn more about our resume templates"
						>
							<Info className="h-4 w-4 text-primary-600" />
						</TooltipTrigger>
						<TooltipContent variant="plain">
							Click to learn more about our professionally crafted templates.
						</TooltipContent>
					</Tooltip>
				</p>
			</div>
			<div className="grid grid-cols-2 gap-3 md:grid-cols-4">
				{listOfExperience
					.filter((ex) => ex.showInList === true)
					.map((experience) => (
						<button
							key={experience.name}
							type="button"
							onClick={() => onSelectionClick(experience.name)}
							className="w-full cursor-pointer rounded-xl border border-gray-200 bg-white p-8 text-center font-semibold text-gray-900 shadow-sm transition-colors hover:border-primary-500 hover:ring-2 hover:ring-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
						>
							{experience.name}
						</button>
					))}
			</div>
			{showTemplates && (
				<div className="mt-12">
					<div className="mx-auto mb-8 max-w-3xl text-center md:mb-12">
						<h2 className="mb-3 text-2xl font-bold text-gray-900">Resume Templates for Every Career Path.</h2>
						<p className="text-gray-600">
							Choose from our professionally designed templates and build your effective resume instantly. Our templates
							are suitable for any professions and industries. Highlights key skills, qualifications, and present your
							credentials in a professionally appealing way. <br />
							Helping you to make a great first impression, even if you&#39;re new to resume writing!
						</p>
					</div>
					<div className="grid grid-cols-1 gap-6 md:grid-cols-3">
						{careerPathTemplates.map((careerPath) => (
							<div key={careerPath.title} className="rounded-xl bg-gray-50 p-4">
								<div className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white p-2">
									<img src={careerPath.image} className="w-full" alt={careerPath.title} />
								</div>
								<h4 className="mb-1 font-semibold text-gray-900">{careerPath.title}</h4>
								<p className="text-sm text-gray-600">{careerPath.description}</p>
							</div>
						))}
					</div>
					<div className="mt-8 text-center">
						<a href="/resume/select-template" className={buttonVariants()}>
							Discover More Resume Templates
						</a>
					</div>
				</div>
			)}
		</>
	);
}

export default SelectExperience;
